import React, { useState, useEffect } from 'react';
import { ClipboardList } from 'lucide-react';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import { formatLeaveDays } from '../../utils/numberUtils';

const LeaveEntitlementCard = ({ 
  title,
  icon: Icon,
  items = [], 
  leaveTypes = [], 
  category, 
  onAddNew, 
  onEdit, 
  onDelete 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const itemsPerPage = 5;

  const filteredItems = items?.filter(item => {
    if (!localSearchTerm) return true;
    const searchableText = [
      item.employment_classification,
      item.leave_type_name,
      item.entitlement_days?.toString(),
    ].filter(Boolean).join(' ').toLowerCase();
    return searchableText.includes(localSearchTerm.toLowerCase());
  }) || [];

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [localSearchTerm]);

  const renderListView = () => (
    <>
      <div className="space-y-3">
        {displayItems.map((item, index) => (
          <div key={item.id || index} className="group border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:bg-gray-50/50 transition-all">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-medium text-gray-900">
                    {item.leave_type_name}
                  </h4>
                  <ClassificationBadge
                    classification={item.employment_classification || 'staff'}
                    label={item.employment_classification ? undefined : 'Unknown'}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <div className="text-gray-600">
                    <span className="font-medium text-gray-700">Leave Type:</span>{' '}
                    <span className="text-gray-900">{item.leave_type_name}</span>
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium text-gray-700">Entitlement:</span>{' '}
                    <span className="text-gray-900 font-medium">{formatLeaveDays(item.entitlement_days)} days</span>
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium text-gray-700">Carry Forward:</span>{' '}
                    <span className={`font-medium ${item.carry_forward_allowed ? 'text-green-600' : 'text-red-600'}`}>
                      {item.carry_forward_allowed ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                {item.maximum_entitlement_days && (
                  <div className="mt-1 text-sm text-amber-600">
                    <span className="font-medium">Max Cap:</span> {formatLeaveDays(item.maximum_entitlement_days)} days
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                  onClick={() => onEdit(category, item)}
                  className="icon-button-admin"
                  title="Edit Entitlement"
                  aria-label="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => onDelete(category, item)}
                  className="icon-button-admin"
                  title="Delete Entitlement"
                  aria-label="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredItems.length)} of {filteredItems.length} entitlements
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn btn-admin-outline btn-sm"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn btn-admin-outline btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderEmptyState = () => (
    <div className="text-center py-12">
      <ClipboardList className="mx-auto w-16 h-16 text-gray-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">No leave entitlements configured</h3>
      <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
        Leave entitlements define how many days each employment classification gets for each leave type.
      </p>
      
      <button
        onClick={() => onAddNew(category)}
        className="btn btn-admin-primary"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Create First Leave Entitlement
      </button>
      
      <div className="mt-8 bg-red-50 rounded-2xl p-4 max-w-md mx-auto text-left">
        <h4 className="text-sm font-medium text-red-900 mb-2">Quick Tips</h4>
        <ul className="text-xs text-red-800 space-y-1">
          <li className="flex items-start gap-2"><span>•</span><span>Start with Annual Leave for each classification</span></li>
          <li className="flex items-start gap-2"><span>•</span><span>Add Sick Leave entitlements</span></li>
          <li className="flex items-start gap-2"><span>•</span><span>Configure carry-forward rules</span></li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 flex-shrink-0"
              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
            >
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {Icon && <Icon className="w-5 h-5 text-gray-500 flex-shrink-0" />}
            <h3 className="text-lg font-semibold text-gray-900 truncate">{title}</h3>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex-shrink-0">
              {filteredItems.length} items
            </span>
          </div>
          
          <button
            onClick={() => onAddNew(category)}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0 ml-3"
            title="Add entitlement"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="hidden sm:inline">Add Entitlement</span>
          </button>
        </div>
        
        {!isCollapsed && items?.length > 0 && (
          <div className="mt-3">
            <DebouncedSearchInput
              value={localSearchTerm}
              onSearch={setLocalSearchTerm}
              placeholder="Search leave entitlements..."
            />
          </div>
        )}
      </div>
      
      {/* Content */}
      {!isCollapsed && (
        <div className="px-6 py-4">
          {filteredItems.length > 0 ? (
            renderListView()
          ) : (
            localSearchTerm ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
                <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                  No entitlements found matching "<span className="font-medium">{localSearchTerm}</span>".
                </p>
                <button
                  onClick={() => setLocalSearchTerm('')}
                  className="mt-6 btn btn-admin-outline"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Search
                </button>
              </div>
            ) : renderEmptyState()
          )}
        </div>
      )}
    </div>
  );
};

export default LeaveEntitlementCard;
